import { notifyModalOpen, notifyModalClose } from '../utils/modalState';
import React, { useState, useEffect, useMemo } from 'react';
import { X, TrendingUp, Users, UserX, Target } from 'lucide-react';
import { MobileSelect } from './ui/MobileSelect';
import type { Player } from '../types';
import { INJURY_STATUS } from '../constants/injuries';
import { InjuryBadge } from './ui/InjuryBadge';
import type { InjuryStatus } from '../constants/injuries';

interface OptimizeSettings {
  absentPlayerIds?: string[];
  opposingPitcherHand?: 'L' | 'R' | null;
  strategy?: 'obp_avg' | 'power' | 'contact' | 'balanced';
  lineupSizeMode?: 'top9' | 'everyone';
}

interface OptimizeLineupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (settings: OptimizeSettings) => void;
  isOptimizing: boolean;
  players: Player[];
  currentLineup: Array<{ order: number; player: Player | null }>;
  playerStatsMap?: Map<string, { batting_average?: number; on_base_percentage?: number; batting_hand?: 'L' | 'R' | 'S' }>;
}

const OptimizeLineupModal: React.FC<OptimizeLineupModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isOptimizing,
  players,
  currentLineup,
  playerStatsMap
}) => {
  const [settings, setSettings] = useState<OptimizeSettings>({
    absentPlayerIds: [],
    opposingPitcherHand: null,
    strategy: 'obp_avg',
    lineupSizeMode: 'everyone',
  });

  // Auto-mark absent: players who are OUT, plus all subs, when modal opens
  useEffect(() => {
    if (isOpen) {
      notifyModalOpen();
      return () => notifyModalClose();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const defaultAbsentIds = players
        .filter(player => {
          const isOut = player.injury_status === INJURY_STATUS.OUT;
          const isSub = (player as Player & { is_sub?: boolean }).is_sub ?? false;
          return isOut || isSub;
        })
        .map(player => player.id);

      setSettings(prev => ({
        ...prev,
        // Keep anything already marked absent, but ensure OUT + subs are always included by default
        absentPlayerIds: Array.from(new Set([...(prev.absentPlayerIds || []), ...defaultAbsentIds]))
      }));
    }
  }, [isOpen, players]);

  const toggleAbsentPlayer = (playerId: string) => {
    const currentAbsent = settings.absentPlayerIds || [];
    const newAbsentIds = currentAbsent.includes(playerId)
      ? currentAbsent.filter(id => id !== playerId)
      : [...currentAbsent, playerId];
    
    setSettings({ ...settings, absentPlayerIds: newAbsentIds });
  };

  const availablePlayers = useMemo(() => {
    return players.filter(p => !(settings.absentPlayerIds || []).includes(p.id));
  }, [players, settings.absentPlayerIds]);

  const getPlayerStats = (player: Player) => {
    // First try to get from statsMap (calculated from sport_stats)
    const statsFromMap = playerStatsMap?.get(player.id);
    
    return {
      avg: statsFromMap?.batting_average || player.batting_average || 0,
      obp: statsFromMap?.on_base_percentage || player.on_base_percentage || 0,
      hand: statsFromMap?.batting_hand || player.batting_hand || 'R'
    };
  };

  // Calculate optimal lineup preview
  const previewLineup = useMemo(() => {
    if (availablePlayers.length === 0) return [];

    const playersWithStats = availablePlayers.map(player => ({
      player,
      stats: getPlayerStats(player)
    }));

    let sorted: typeof playersWithStats = [];

    switch (settings.strategy) {
      case 'obp_avg':
        // Sort by OBP (primary), then AVG (secondary)
        sorted = [...playersWithStats].sort((a, b) => {
          if (b.stats.obp !== a.stats.obp) return b.stats.obp - a.stats.obp;
          return b.stats.avg - a.stats.avg;
        });
        break;
      case 'power':
        // Sort by AVG (power hitters typically have higher AVG)
        sorted = [...playersWithStats].sort((a, b) => b.stats.avg - a.stats.avg);
        break;
      case 'contact':
        // Sort by OBP (contact hitters get on base)
        sorted = [...playersWithStats].sort((a, b) => b.stats.obp - a.stats.obp);
        break;
      case 'balanced':
        // Mix of OBP and AVG with consideration for pitcher hand
        sorted = [...playersWithStats].sort((a, b) => {
          // If pitcher hand is specified, prefer opposite hand batters
          if (settings.opposingPitcherHand) {
            const aAdvantage = (settings.opposingPitcherHand === 'L' && a.stats.hand === 'R') || 
                              (settings.opposingPitcherHand === 'R' && a.stats.hand === 'L') ? 1.1 : 1;
            const bAdvantage = (settings.opposingPitcherHand === 'L' && b.stats.hand === 'R') || 
                              (settings.opposingPitcherHand === 'R' && b.stats.hand === 'L') ? 1.1 : 1;
            const aScore = (a.stats.obp * 0.6 + a.stats.avg * 0.4) * aAdvantage;
            const bScore = (b.stats.obp * 0.6 + b.stats.avg * 0.4) * bAdvantage;
            return bScore - aScore;
          }
          // No pitcher hand specified, use OBP/AVG mix
          const aScore = a.stats.obp * 0.6 + a.stats.avg * 0.4;
          const bScore = b.stats.obp * 0.6 + b.stats.avg * 0.4;
          return bScore - aScore;
        });
        break;
    }

    // If pitcher hand is specified and strategy isn't balanced, adjust for hand advantage
    if (settings.opposingPitcherHand && settings.strategy !== 'balanced') {
      sorted = sorted.map(p => {
        const advantage = (settings.opposingPitcherHand === 'L' && p.stats.hand === 'R') || 
                         (settings.opposingPitcherHand === 'R' && p.stats.hand === 'L');
        return { ...p, advantage };
      }).sort((a, b) => {
        // Primary sort by strategy
        let comparison = 0;
        if (settings.strategy === 'obp_avg') {
          if (b.stats.obp !== a.stats.obp) comparison = b.stats.obp - a.stats.obp;
          else comparison = b.stats.avg - a.stats.avg;
        } else if (settings.strategy === 'power') {
          comparison = b.stats.avg - a.stats.avg;
        } else {
          comparison = b.stats.obp - a.stats.obp;
        }
        
        // Secondary sort: prefer opposite hand if similar stats
        if (Math.abs(comparison) < 0.05) {
          if (a.advantage && !b.advantage) return -1;
          if (!a.advantage && b.advantage) return 1;
        }
        return comparison;
      });
    }

    const maxSlots =
      settings.lineupSizeMode === 'top9'
        ? Math.min(availablePlayers.length, 9)
        : availablePlayers.length;

    return sorted.slice(0, maxSlots).map((item, index) => ({
      order: index + 1,
      player: item.player,
      stats: item.stats
    }));
  }, [availablePlayers, settings.strategy, settings.opposingPitcherHand, settings.lineupSizeMode]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[9999] touch-none"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0)',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      <div
        className="bg-white rounded-t-xl sm:rounded-xl shadow-xl max-w-4xl w-full animate-slide-up sm:animate-none flex flex-col overflow-hidden"
        style={{
          maxHeight: 'min(85vh, 85dvh)',
        }}
      >
        <div className="contents">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-stone border-opacity-20 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="bg-primary bg-opacity-10 p-2 rounded-lg">
                <TrendingUp className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-dark">Optimize Batting Order</h3>
                <p className="text-xs sm:text-sm text-gray-600">Generate optimal lineup based on player stats and strategy</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-cream rounded-lg transition-colors touch-manipulation"
              disabled={isOptimizing}
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div 
            className="flex-1 overflow-y-auto min-h-0 overscroll-contain -webkit-overflow-scrolling-touch"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {/* Optimize Strategy Section */}
            <div className="p-4 sm:p-6 border-b border-stone border-opacity-20">
              <h4 className="text-sm font-medium text-dark mb-4 flex items-center">
                <Target className="h-4 w-4 mr-2" />
                Optimization Strategy
              </h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Strategy
                  </label>
                  <MobileSelect
                    value={settings.strategy}
                    onChange={(v) => setSettings({ ...settings, strategy: v as OptimizeSettings['strategy'] })}
                    options={[
                      { value: 'obp_avg', label: 'OBP & AVG (Recommended)' },
                      { value: 'power', label: 'Power Hitting (AVG Focus)' },
                      { value: 'contact', label: 'Contact Hitting (OBP Focus)' },
                      { value: 'balanced', label: 'Balanced (OBP/AVG + Pitcher Matchup)' },
                    ]}
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                    disabled={isOptimizing}
                    aria-label="Optimization strategy"
                    sheetTitle="Optimization Strategy"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Opposing Pitcher Hand
                  </label>
                  <MobileSelect
                    value={settings.opposingPitcherHand || ''}
                    onChange={(v) => setSettings({
                      ...settings,
                      opposingPitcherHand: v ? (v as 'L' | 'R') : null,
                    })}
                    options={[
                      { value: '', label: 'Unknown / Not Specified' },
                      { value: 'L', label: 'Left-Handed Pitcher (LHP)' },
                      { value: 'R', label: 'Right-Handed Pitcher (RHP)' },
                    ]}
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                    disabled={isOptimizing}
                    aria-label="Opposing pitcher hand"
                    sheetTitle="Opposing Pitcher Hand"
                  />
                  {settings.opposingPitcherHand && (
                    <p className="text-xs text-gray-600 mt-1">
                      {settings.opposingPitcherHand === 'L' 
                        ? 'Right-handed batters will be prioritized' 
                        : 'Left-handed batters will be prioritized'}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Lineup Size
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => setSettings({ ...settings, lineupSizeMode: 'everyone' })}
                      disabled={isOptimizing}
                      className={`px-3 py-1.5 rounded border ${
                        settings.lineupSizeMode === 'everyone'
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Everyone bats
                    </button>
                    <button
                      type="button"
                      onClick={() => setSettings({ ...settings, lineupSizeMode: 'top9' })}
                      disabled={isOptimizing}
                      className={`px-3 py-1.5 rounded border ${
                        settings.lineupSizeMode === 'top9'
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Top 9 only
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-600 mt-1">
                    Most youth leagues bat everyone. Use “Top 9 only” for tournaments or stricter rule sets.
                  </p>
                </div>
              </div>
            </div>

            {/* Preview Section */}
            {previewLineup.length > 0 && (
              <div className="p-4 sm:p-6 border-b border-stone border-opacity-20">
                <h4 className="text-sm font-medium text-dark mb-3 flex items-center">
                  <Users className="h-4 w-4 mr-2" />
                  Optimized Lineup Preview
                </h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {previewLineup.map(({ order, player, stats }) => (
                    <div key={player?.id ?? order} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm min-w-[2rem]">#{order}</span>
                        <span className="text-sm">
                          {player?.number != null ? `#${player.number} ` : ''}
                          {player?.first_name?.charAt(0) ?? ''}. {player?.last_name ?? ''}
                        </span>
                        <span className="text-xs font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                          {stats.hand === 'S' ? 'S' : stats.hand === 'L' ? 'L' : 'R'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-600">
                        <div className="flex flex-col items-end">
                          <span>OBP: {stats.obp > 0 ? stats.obp.toFixed(3) : '---'}</span>
                          <span>AVG: {stats.avg > 0 ? stats.avg.toFixed(3) : '---'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Unavailable Players Section */}
            <div className="p-4 sm:p-6 border-t border-stone border-opacity-20">
              <h4 className="text-sm font-medium text-dark mb-3 flex items-center">
                <UserX className="h-4 w-4 mr-2 text-red-500" />
                Unavailable Players
              </h4>
              <p className="text-xs text-gray-600 mb-3 sm:mb-4">
                Select players who won't be at the game. They will be excluded from optimization.
              </p>
              
              <div className="space-y-2 max-h-48 sm:max-h-64 overflow-y-auto">
                {players.map(player => {
                  const isOut = player.injury_status === INJURY_STATUS.OUT;
                  const isUnavailable = (settings.absentPlayerIds || []).includes(player.id);
                  return (
                    <label 
                      key={player.id} 
                      className={`flex items-center gap-2 p-2 sm:p-2.5 rounded hover:bg-gray-50 cursor-pointer ${
                        isOut ? 'bg-red-50 border border-red-200' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isUnavailable}
                        onChange={() => toggleAbsentPlayer(player.id)}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500 flex-shrink-0"
                        disabled={isOptimizing || isOut}
                      />
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <span className={`text-xs sm:text-sm truncate ${isOut ? 'text-red-700 font-medium' : ''}`}>
                          #{player?.number ?? ''} {player?.first_name?.charAt(0) ?? ''}. {player?.last_name ?? ''}
                        </span>
                        {player.injury_status && player.injury_status !== 'H' && (
                          <InjuryBadge 
                            injuryStatus={player.injury_status as InjuryStatus} 
                            size="xs" 
                            className="flex-shrink-0"
                          />
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
              
              {(settings.absentPlayerIds || []).length > 0 && (
                <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs text-red-700">
                    {(settings.absentPlayerIds || []).length} player(s) marked as unavailable
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 p-4 sm:p-6 border-t border-stone border-opacity-20 flex-shrink-0 bg-white">
            <button
              onClick={onClose}
              className="flex-1 px-3 sm:px-4 py-3 sm:py-2 border border-stone border-opacity-20 rounded-xl text-dark hover:bg-cream transition-colors text-sm sm:text-base touch-manipulation min-h-[44px]"
              disabled={isOptimizing}
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(settings)}
              disabled={isOptimizing || availablePlayers.length === 0}
              className="flex-1 px-3 sm:px-4 py-3 sm:py-2 bg-primary text-white rounded-xl hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm sm:text-base touch-manipulation min-h-[44px]"
            >
              {isOptimizing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Optimizing...
                </>
              ) : (
                <>
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Optimize Lineup
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OptimizeLineupModal;

